/**
 * CENTRALIZED COLOR SCHEME CONFIGURATION
 * 
 * This file defines all color schemes for the app.
 * Switch between schemes by changing the ACTIVE_SCHEME constant.
 * 
 * Easy to experiment with seasonal themes:
 * - Default (Purple/Blue gradient)
 * - Clean White
 * - Fall Colors (coming soon)
 * - Halloween (coming soon)
 * - Winter Wonderland (coming soon)
 */

export type ColorScheme = {
  name: string;
  description: string;
  colors: {
    // Main background colors
    background: string; // HSL format: "0 0% 98%"
    foreground: string;
    
    // Card colors
    card: string;
    cardForeground: string;
    
    // Banner/Navigation colors
    navBackground: string; // The top banner color
    navForeground: string;
    
    // Primary brand colors
    primary: string;
    primaryForeground: string;
    
    // Secondary colors
    secondary: string;
    secondaryForeground: string;
    
    // Accent colors
    accent: string;
    accentForeground: string;
    
    // Muted colors
    muted: string;
    mutedForeground: string;
    
    // Border and input
    border: string;
    input: string;
    ring: string;
    
    // Gradients
    gradientPrimary: string;
    gradientSecondary: string;
    gradientHero: string; // Main background gradient (Landing page)
    gradientBackground: string; // Light page background (Dashboard, etc.)
  };
};


// ============================================
// SCHEME 1: COSMIC PURPLE (Lighter Version)
// ============================================
{/*export const cosmicPurpleScheme: ColorScheme = {
  name: "Cosmic Purple",
  description: "Lighter purple-to-blue gradient with mystical vibes",
  colors: {
    // Light backgrounds
    background: "0 0% 98%",
    foreground: "240 30% 8%",
    
    // Cards
    card: "0 0% 100%",
    cardForeground: "240 30% 8%",
    
    // Banner - Dark blue like in reference images
    navBackground: "233 47% 17%", // Dark blue from images
    navForeground: "0 0% 100%",
    
    // Primary Purple/Indigo
    primary: "262 83% 58%",
    primaryForeground: "0 0% 100%",
    
    // Secondary Amber/Gold
    secondary: "45 93% 58%",
    secondaryForeground: "240 30% 8%",
    
    // Accent
    accent: "262 83% 58%",
    accentForeground: "0 0% 100%",
    
    // Muted
    muted: "240 10% 96%",
    mutedForeground: "240 9% 45%",
    
    // Borders
    border: "240 10% 92%",
    input: "240 10% 95%",
    ring: "262 83% 58%",
    
    // Gradients - LIGHTER purple-to-blue gradient
    gradientPrimary: "linear-gradient(135deg, hsl(262 83% 58%), hsl(280 70% 50%))",
    gradientSecondary: "linear-gradient(135deg, hsl(45 93% 58%), hsl(35 93% 55%))",
    gradientHero: "linear-gradient(180deg, hsl(252 70% 50%), hsl(235 65% 45%))", // Much lighter purple-to-blue
  },
};
*/}

// ============================================
// SCHEME 1: COSMIC PURPLE (Darker Version)
// ============================================
// ============================================
// SCHEME 1: COSMIC PURPLE (Customized)
// ============================================
// ============================================
// SCHEME 1: COSMIC PURPLE (Customized)
// ============================================
export const cosmicPurpleScheme: ColorScheme = {
  name: "Cosmic Purple",
  description: "Deep violet-to-dark-purple gradient with mystical vibes",
  colors: {
    // Light backgrounds
    background: "0 0% 98%",
    foreground: "240 30% 8%",
    
    // Cards
    card: "0 0% 100%",
    cardForeground: "240 30% 8%",
    
    // Banner - UPDATED: Lighter "Royal Navy" Blue (Matches your image)
    // Previous: "235 50% 20%" (Too dark)
    navBackground: "225 45% 32%", 
    navForeground: "0 0% 100%",
    
    // Primary Purple/Indigo
    primary: "262 83% 58%",
    primaryForeground: "0 0% 100%",
    
    // Secondary Amber/Gold
    secondary: "45 93% 58%",
    secondaryForeground: "240 30% 8%",
    
    // Accent
    accent: "262 83% 58%",
    accentForeground: "0 0% 100%",
    
    // Muted
    muted: "240 10% 96%",
    mutedForeground: "240 9% 45%",
    
    // Borders
    border: "240 10% 92%",
    input: "240 10% 95%",
    ring: "262 83% 58%",
    
    // Gradients
    gradientPrimary: "linear-gradient(135deg, hsl(262 83% 58%), hsl(280 70% 50%))",
    gradientSecondary: "linear-gradient(135deg, hsl(45 93% 58%), hsl(35 93% 55%))",
    
    // Hero Gradient - (Kept the lighter purple you requested previously)
    gradientHero: "linear-gradient(180deg, hsl(255 60% 35%), hsl(245 55% 28%))",
    
    // Light page background (Dashboard, protected pages)
    gradientBackground: "linear-gradient(180deg, hsl(0 0% 98%), hsl(240 10% 96%), hsl(280 10% 97%))",
  },
};
// ============================================
// SCHEME 2: CLEAN WHITE
// ============================================
export const cleanWhiteScheme: ColorScheme = {
  name: "Clean White",
  description: "Pure white background with dark blue banner",
  colors: {
    // White backgrounds
    background: "0 0% 100%",
    foreground: "240 30% 8%",
    
    // Cards
    card: "0 0% 100%",
    cardForeground: "240 30% 8%",
    
    // Banner - Same dark blue as cosmic purple
    navBackground: "233 47% 17%",
    navForeground: "0 0% 100%",
    
    // Primary Purple/Indigo
    primary: "262 83% 58%",
    primaryForeground: "0 0% 100%",
    
    // Secondary Amber/Gold
    secondary: "45 93% 58%",
    secondaryForeground: "240 30% 8%",
    
    // Accent
    accent: "262 83% 58%",
    accentForeground: "0 0% 100%",
    
    // Muted
    muted: "240 10% 96%",
    mutedForeground: "240 9% 45%",
    
    // Borders
    border: "240 10% 92%",
    input: "240 10% 95%",
    ring: "262 83% 58%",
    
    // Gradients - No hero gradient, just white
    gradientPrimary: "linear-gradient(135deg, hsl(262 83% 58%), hsl(280 70% 50%))",
    gradientSecondary: "linear-gradient(135deg, hsl(45 93% 58%), hsl(35 93% 55%))",
    gradientHero: "linear-gradient(180deg, hsl(0 0% 100%), hsl(0 0% 100%))", // Pure white
    
    // Light page background (same as hero for clean white)
    gradientBackground: "linear-gradient(180deg, hsl(0 0% 100%), hsl(0 0% 100%))",
  },
};

// ============================================
// FUTURE SCHEMES (Templates for seasonal themes)
// ============================================

// Uncomment and customize these for seasonal themes!

/*
export const fallColorsScheme: ColorScheme = {
  name: "Fall Colors",
  description: "Warm autumn vibes with orange and brown tones",
  colors: {
    background: "0 0% 98%",
    foreground: "30 10% 20%",
    card: "0 0% 100%",
    cardForeground: "30 10% 20%",
    navBackground: "25 50% 25%", // Deep brown
    navForeground: "0 0% 100%",
    primary: "25 85% 55%", // Warm orange
    primaryForeground: "0 0% 100%",
    secondary: "45 75% 50%", // Golden yellow
    secondaryForeground: "30 10% 20%",
    accent: "15 85% 55%",
    accentForeground: "0 0% 100%",
    muted: "30 10% 96%",
    mutedForeground: "30 10% 45%",
    border: "30 10% 92%",
    input: "30 10% 95%",
    ring: "25 85% 55%",
    gradientPrimary: "linear-gradient(135deg, hsl(25 85% 55%), hsl(35 85% 50%))",
    gradientSecondary: "linear-gradient(135deg, hsl(45 75% 50%), hsl(35 75% 45%))",
    gradientHero: "linear-gradient(180deg, hsl(30 40% 35%), hsl(25 45% 30%))",
  },
};

export const halloweenScheme: ColorScheme = {
  name: "Halloween",
  description: "Spooky orange and purple with dark backgrounds",
  colors: {
    background: "0 0% 98%",
    foreground: "280 10% 10%",
    card: "0 0% 100%",
    cardForeground: "280 10% 10%",
    navBackground: "280 40% 15%", // Dark purple
    navForeground: "0 0% 100%",
    primary: "35 100% 50%", // Bright orange
    primaryForeground: "0 0% 100%",
    secondary: "280 60% 50%", // Purple
    secondaryForeground: "0 0% 100%",
    accent: "35 100% 50%",
    accentForeground: "0 0% 100%",
    muted: "280 10% 96%",
    mutedForeground: "280 10% 45%",
    border: "280 10% 92%",
    input: "280 10% 95%",
    ring: "35 100% 50%",
    gradientPrimary: "linear-gradient(135deg, hsl(35 100% 50%), hsl(25 100% 45%))",
    gradientSecondary: "linear-gradient(135deg, hsl(280 60% 50%), hsl(270 60% 45%))",
    gradientHero: "linear-gradient(180deg, hsl(280 40% 15%), hsl(35 50% 20%))",
  },
};
*/

// ============================================
// ACTIVE SCHEME SELECTOR
// ============================================

/**
 * CHANGE THIS TO SWITCH COLOR SCHEMES!
 * 
 * Available schemes:
 * - cosmicPurpleScheme (default - lighter purple gradient)
 * - cleanWhiteScheme (white background)
 * 
 * To add seasonal themes, uncomment the schemes above and add them here.
 */
export const ACTIVE_SCHEME: ColorScheme = cosmicPurpleScheme;

// Helper to get all available schemes (for potential theme switcher UI)
export const ALL_SCHEMES = {
  cosmicPurple: cosmicPurpleScheme,
  cleanWhite: cleanWhiteScheme,
  // Uncomment when ready to use:
  // fall: fallColorsScheme,
  // halloween: halloweenScheme,
};
